import type { SiteConfigEntry } from "@config/site/types";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { Consents } from "@lib/constants/contentful/consent";
import { useApi } from "@lib/helpers/useApi";
import { generateRecaptchaToken } from "@lib/utils/recaptcha/generateRecaptchaToken";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { EMAIL_SUBSCRIPTION_API_PATH, RECAPTCHA_ACTION } from "../constants";
import type { NewsletterRequest, NewsletterResponse, Values } from "../types";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useLocale } from "next-intl";

type Config = {
    reCaptchaIntegration: SiteConfigEntry["reCaptchaIntegration"];
    error: {
        title: string;
        description: string;
    };
};

export const useSubmit = ({ reCaptchaIntegration, error }: Config) => {
    const locale = useLocale();
    const queryClient = useQueryClient();

    const { description, title } = error;

    const errorToaster = useErrorToast(2000);

    const onSuccess = (data: NewsletterResponse) => {
        AnalyticsClient.newsletterSubscribe(data.email);
        queryClient.invalidateQueries({ queryKey: [QueryKeys.getProfile] });
    };

    const onError = () =>
        errorToaster({
            title,
            description,
        });

    const {
        mutateAsync: newsletterMutation,
        isPending,
        isSuccess,
    } = useApi.useMutate<NewsletterRequest, NewsletterResponse>(`${EMAIL_SUBSCRIPTION_API_PATH}`, {
        method: "POST",
        onError,
        onSuccess,
    });

    const onSubmit = async (values: Values) => {
        const recaptchaToken = reCaptchaIntegration
            ? await generateRecaptchaToken(RECAPTCHA_ACTION)
            : null;

        await newsletterMutation({
            email: values.email,
            name: values.name,
            newsletterKey: Consents.newsletter,
            privacyPolicyKey: Consents.privacyPolicy,
            termsAndConditionsKey: Consents.termsAndConditions,
            locale,
            recaptchaToken,
        });
    };

    return {
        onSubmit,
        isPending,
        isSuccess,
    };
};
